import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { DashboardSidebar } from '../components/layout/DashboardSidebar';
import { apiFetch } from '../lib/apiClient';
import {
  BookOpen,
  Clock,
  Play,
  CheckCircle2,
  Lock,
  ChevronRight,
  TrendingUp,
  Target,
  Award,
  Flame,
  ArrowUpRight,
} from 'lucide-react';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [progressDashboard, setProgressDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadProfile = async () => {
      try {
        const response = await apiFetch('/auth/profile', { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          const message = data?.message || data?.error || 'Unable to load profile.';
          throw new Error(message);
        }

        if (isMounted) {
          const loadedProfile = data.profile || data;
          
          if (!loadedProfile.currentRoadmap || Object.keys(loadedProfile.currentRoadmap).length === 0) {
            navigate('/dashboard/onboarding/upload');
            return;
          }
          
          setProfile(loadedProfile);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Unable to load profile.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  useEffect(() => {
    let isMounted = true;

    const loadProgressDashboard = async () => {
      try {
        const response = await apiFetch('/progress/dashboard', { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (response.ok && isMounted) {
          setProgressDashboard(data.dashboard || data);
        }
      } catch {
        // Milestones section will show empty fallback
      }
    };

    loadProgressDashboard();

    return () => {
      isMounted = false;
    };
  }, []);

  const defaultRoadmap = {
    styleTrack: 'Concept Art',
    currentModule: {
      id: 'm2',
      title: 'Fundamentals of Perspective',
      description: 'Master 1, 2, and 3-point perspective for dynamic environments',
      progress: 45,
      totalLessons: 12,
      completedLessons: 5,
      estimatedTime: '3 hours remaining',
      nextLesson: {
        title: 'Two-Point Perspective Basics',
        duration: '30 min',
        type: 'exercise',
        skillTargets: ['Perspective', 'Composition'],
      },
    },
    modules: [
      { id: 'm1', title: 'Drawing Fundamentals', progress: 100, status: 'completed', lessons: 8 },
      { id: 'm2', title: 'Fundamentals of Perspective', progress: 45, status: 'current', lessons: 12 },
      { id: 'm3', title: 'Color Theory & Value', progress: 0, status: 'locked', lessons: 10 },
      { id: 'm4', title: 'Environment Design', progress: 0, status: 'locked', lessons: 15 },
      { id: 'm5', title: 'Lighting & Atmosphere', progress: 0, status: 'locked', lessons: 12 },
      { id: 'm6', title: 'Portfolio Project', progress: 0, status: 'locked', lessons: 6 },
    ],
    recentLessons: [
      { title: 'One-Point Perspective', module: 'Perspective', completed: true, date: 'Today' },
      { title: 'Line Confidence Drills', module: 'Fundamentals', completed: true, date: 'Yesterday' },
      { title: 'Basic Shape Construction', module: 'Fundamentals', completed: true, date: '2 days ago' },
    ],
  };

  const resolvedRoadmap = (() => {
    const roadmap = profile?.currentRoadmap;
    const exercises = roadmap?.availableExercises || [];

    if (!roadmap || !exercises.length) {
      return defaultRoadmap;
    }

    const primary = exercises[0];

    return {
      styleTrack: roadmap.styleTrack || profile?.styleTrack || defaultRoadmap.styleTrack,
      currentModule: {
        id: primary.exerciseId,
        title: primary.title,
        description: primary.instructions,
        progress: 0,
        totalLessons: exercises.length,
        completedLessons: (roadmap.completedExercises || []).length,
        estimatedTime: `${primary.duration} min`,
        nextLesson: {
          title: primary.title,
          duration: `${primary.duration} min`,
          type: 'exercise',
          skillTargets: primary.targetSkills || [],
        },
      },
      modules: exercises.map((exercise, index) => ({
        id: exercise.exerciseId,
        title: exercise.title,
        progress: 0,
        status: index === 0 ? 'current' : 'locked',
        lessons: exercise.learningObjectives?.length || 1,
      })),
      recentLessons: defaultRoadmap.recentLessons,
    };
  })();

  const stats = (() => {
    const streakCurrent = parseInt(profile?.streak?.current || '0', 10);
    const totalMinutes = parseInt(profile?.statistics?.totalPracticeMinutes || '0', 10);
    const totalExercises = parseInt(profile?.statistics?.totalExercisesCompleted || '0', 10);

    const skillMetrics = profile?.skillGapReport?.skillMetrics || {};
    const entries = Object.values(skillMetrics).map((v) => Number(v || 0));
    const average = entries.length
      ? entries.reduce((sum, v) => sum + v, 0) / entries.length
      : 0;

    let skillLevelLabel = 'Beginner';
    if (average >= 8) skillLevelLabel = 'Advanced';
    else if (average >= 6) skillLevelLabel = 'Upper Intermediate';
    else if (average >= 4) skillLevelLabel = 'Intermediate';

    let practiceDisplay = '0h';
    if (totalMinutes > 0 && totalMinutes < 60) {
      practiceDisplay = `${totalMinutes}m`;
    } else {
      practiceDisplay = `${Math.round(totalMinutes / 60)}h`;
    }

    return {
      streak: streakCurrent || 0,
      practiceDisplay,
      lessonsCompleted: totalExercises || 0,
      skillLevel: skillLevelLabel,
    };
  })();

  const upcomingMilestones = (progressDashboard?.milestones || []).map((m, idx) => ({
    title: m.description || m.type || 'Milestone',
    reward: m.type || (m.achievedAt ? 'Achieved' : ''),
    progress: typeof m.progress === 'number' ? m.progress : (m.achievedAt ? 100 : 0),
  }));

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-4 h-4 text-[hsl(142,76%,36%)]" />;
      case 'current': return <Play className="w-4 h-4 text-[hsl(234,89%,63%)]" />;
      default: return <Lock className="w-4 h-4 text-slate-400" />;
    }
  };

  const recentCompletedExercises = (() => {
    const roadmap = profile?.currentRoadmap;
    const completed = roadmap?.completedExercises || [];
    const available = roadmap?.availableExercises || [];

    if (!completed.length) {
      return [];
    }

    // Take the most recent up to 5 completed items
    const lastFive = completed.slice(-5).reverse();

    return lastFive.map((item) => {
      const isObj = typeof item === 'object' && item !== null;
      const id = isObj ? item.exerciseId : item;
      const defaultTitle = isObj ? item.title : id;
      const defaultTrack = isObj ? item.styleTrack : 'Exercise';

      const match = available.find((ex) => ex.exerciseId === id) || {};

      return {
        title: match.title || defaultTitle || 'Unknown Exercise',
        module: match.styleTrack || defaultTrack || 'Exercise',
        date: 'Completed',
      };
    });
  })();

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(220,20%,97%)]" data-testid="dashboard-loading">
        <DashboardSidebar />
        <main className="lg:ml-60 p-4 pt-24 lg:p-8 space-y-8">
          {/* Header Skeleton */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="animate-pulse">
              <div className="h-8 w-64 bg-slate-200/60 rounded mb-2"></div>
              <div className="h-4 w-48 bg-slate-200/60 rounded"></div>
            </div>
            <div className="h-10 w-32 bg-slate-200/60 rounded-full animate-pulse"></div>
          </div>

          {/* Stats Overview Skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-pulse">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-white rounded-xl border border-slate-100 shadow-sm p-4">
                <div className="flex justify-between items-start">
                  <div className="space-y-3 mt-1">
                    <div className="h-3 w-20 bg-slate-200/60 rounded"></div>
                    <div className="h-6 w-16 bg-slate-200/60 rounded"></div>
                  </div>
                  <div className="w-10 h-10 bg-slate-200/60 rounded-xl"></div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 animate-pulse">
            {/* Left Column Skeleton */}
            <div className="col-span-1 lg:col-span-8 space-y-6">
              {/* Continue Learning Skeleton */}
              <div className="h-56 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden flex flex-col justify-between">
                <div className="h-36 bg-slate-200/60 w-full mb-4 shrink-0"></div>
                <div className="px-5 pb-5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-200/60"></div>
                    <div className="space-y-2">
                       <div className="h-3 w-16 bg-slate-200/60 rounded"></div>
                       <div className="h-4 w-32 bg-slate-200/60 rounded"></div>
                    </div>
                  </div>
                  <div className="h-9 w-24 rounded-full bg-slate-200/60"></div>
                </div>
              </div>

              {/* Roadmap Overview Skeleton */}
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 space-y-2">
                <div className="flex justify-between items-center mb-6">
                  <div className="h-5 w-32 bg-slate-200/60 rounded"></div>
                  <div className="h-5 w-24 bg-slate-200/60 rounded"></div>
                </div>
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-[60px] bg-slate-100/50 rounded-xl border border-slate-100"></div>
                ))}
              </div>
            </div>

            {/* Right Column Skeleton */}
            <div className="col-span-1 lg:col-span-4 space-y-6">
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
                <div className="h-5 w-48 bg-slate-200/60 rounded mb-6"></div>
                <div className="space-y-5">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <div className="w-7 h-7 rounded-lg bg-slate-200/60 mt-1"></div>
                      <div className="space-y-2.5 flex-1">
                        <div className="h-3.5 w-full bg-slate-200/60 rounded"></div>
                        <div className="h-3 w-2/3 bg-slate-200/60 rounded"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
                <div className="h-5 w-32 bg-slate-200/60 rounded mb-6"></div>
                <div className="space-y-6">
                  {[1, 2].map((i) => (
                     <div key={i} className="space-y-3">
                       <div className="flex justify-between">
                         <div className="h-3 w-24 bg-slate-200/60 rounded"></div>
                         <div className="h-3 w-8 bg-slate-200/60 rounded"></div>
                       </div>
                       <div className="h-1.5 w-full bg-slate-200/60 rounded-full"></div>
                     </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(220,20%,97%)]" data-testid="dashboard-page">
      <DashboardSidebar />

      <main className="lg:ml-60 p-4 pt-24 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8" data-testid="dashboard-header">
          <div>
            <h1 className="text-2xl font-bold text-[hsl(222,47%,11%)] font-['Outfit']">
              Welcome back, {profile?.displayName || 'Artist'}!
            </h1>
            <p className="text-slate-600 mt-0.5">
              Continue your {resolvedRoadmap.styleTrack} journey
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm border border-slate-200/60">
              <Flame className="w-4 h-4 text-[hsl(12,95%,65%)] streak-fire" />
              <span className="font-semibold text-[hsl(222,47%,11%)] text-sm">
                {stats.streak} {stats.streak === 1 ? 'day' : 'days'} streak
              </span>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stats-overview">
          {[
            { label: 'Current Streak', value: `${stats.streak} ${stats.streak === 1 ? 'day' : 'days'}`, icon: Flame, color: 'hsl(12,95%,65%)' },
            { label: 'Total Practice', value: stats.practiceDisplay, icon: Clock, color: 'hsl(234,89%,63%)' },
            { label: 'Exercises Done', value: stats.lessonsCompleted, icon: BookOpen, color: 'hsl(262,83%,58%)' },
            { label: 'Skill Level', value: profile?.questionnaire.experienceLevel || 'Beginner', icon: TrendingUp, color: 'hsl(234,89%,63%)' }
          ].map((stat, idx) => (
            <Card key={idx} className="card-elevated border-0" data-testid={`stat-card-${idx}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 mb-0.5">{stat.label}</p>
                    <p className="text-xl font-bold text-[hsl(222,47%,11%)]">{stat.value}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${stat.color.replace(')', ' / 0.1)')}` }}>
                    <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column - Roadmap */}
          <div className="col-span-12 lg:col-span-8 space-y-6">
            {/* Continue Learning Card */}
            <Card className="card-elevated border-0 overflow-hidden" data-testid="continue-learning-card">
              <div className="bg-gradient-to-r from-[hsl(234,89%,63%)] to-[hsl(262,83%,58%)] p-5">
                <div className="flex items-start justify-between">
                  <div className="text-white">
                    <Badge className="bg-white/15 text-white hover:bg-white/25 mb-2 text-xs">Current Exercise</Badge>
                    <h2 className="text-xl font-bold font-['Outfit'] mb-1">
                      {resolvedRoadmap.currentModule.title}
                    </h2>
                    <p className="text-white/75 text-sm">
                      {resolvedRoadmap.currentModule.description}
                    </p>
                  </div>
                  <div className="text-white text-right">
                    <p className="text-3xl font-bold">
                      {resolvedRoadmap.currentModule.progress}%
                    </p>
                    <p className="text-white/75 text-xs">Complete</p>
                  </div>
                </div>
                <div className="mt-4">
                  <Progress
                    value={resolvedRoadmap.currentModule.progress}
                    className="h-1.5 bg-white/20"
                  />
                  <div className="flex justify-between mt-2 text-white/75 text-xs">
                    <span>
                      {resolvedRoadmap.currentModule.completedLessons}/
                      {resolvedRoadmap.currentModule.totalLessons} exercises
                    </span>
                    <span>{resolvedRoadmap.currentModule.estimatedTime}</span>
                  </div>
                </div>
              </div>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[hsl(234,89%,63%)]/10 flex items-center justify-center">
                      <Play className="w-5 h-5 text-[hsl(234,89%,63%)]" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Next Lesson</p>
                      <p className="font-semibold text-[hsl(222,47%,11%)] text-sm">
                        {resolvedRoadmap.currentModule.nextLesson.title}
                      </p>
                      <p className="text-xs text-slate-400">
                        {resolvedRoadmap.currentModule.nextLesson.duration} •{' '}
                        {resolvedRoadmap.currentModule.nextLesson.type}
                      </p>
                    </div>
                  </div>
                  <Button
                    className="bg-[hsl(234,89%,63%)] hover:bg-[hsl(234,89%,58%)] text-white rounded-full text-sm h-9"
                    data-testid="continue-lesson-btn"
                    onClick={() => navigate(`/exercise/${resolvedRoadmap.currentModule.id}`)}
                  >
                    Continue
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Roadmap Overview */}
            <Card className="card-elevated border-0" data-testid="roadmap-overview">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-['Outfit']">Your Roadmap</CardTitle>
                  <Button
                    variant="ghost"
                    className="text-[hsl(234,89%,63%)] hover:text-[hsl(234,89%,58%)] h-8 text-sm"
                    data-testid="view-all-modules"
                    onClick={() => {
                      navigate('/dashboard/roadmap');
                    }}
                  >
                    Open Roadmap
                    <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {resolvedRoadmap.modules.map((module) => (
                    <div
                      key={module.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${module.status === 'current'
                        ? 'border-[hsl(234,89%,63%)] bg-[hsl(234,89%,63%)]/5'
                        : module.status === 'completed'
                          ? 'border-[hsl(142,76%,36%)]/30 bg-[hsl(142,76%,36%)]/5'
                          : 'border-slate-200/60 bg-slate-50/50 opacity-50'
                        }`}
                      data-testid={`module-${module.id}`}
                      onClick={() => navigate(`/exercise/${module.id}`)}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${module.status === 'completed' ? 'bg-[hsl(142,76%,36%)]/10' :
                        module.status === 'current' ? 'bg-[hsl(234,89%,63%)]/10' : 'bg-slate-200'
                        }`}>
                        {getStatusIcon(module.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className={`font-medium text-sm truncate ${module.status === 'locked' ? 'text-slate-400' : 'text-[hsl(222,47%,11%)]'}`}>
                            {module.title}
                          </p>
                          <span className="text-xs text-slate-500 ml-2">{module.lessons} lessons</span>
                        </div>
                        {module.status !== 'locked' && (
                          <Progress value={module.progress} className="h-1" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Progress & Milestones */}
          <div className="col-span-12 lg:col-span-4 space-y-6">
            {/* Recent Activity */}
            <Card className="card-elevated border-0" data-testid="recent-activity">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-['Outfit']">Recent Completed Exercises</CardTitle>
              </CardHeader>
              <CardContent>
                {recentCompletedExercises.length > 0 ? (
                  <div className="space-y-3">
                    {recentCompletedExercises.map((lesson, idx) => (
                      <div key={idx} className="flex items-start gap-2.5" data-testid={`recent-lesson-${idx}`}>
                        <div className="w-7 h-7 rounded-lg bg-[hsl(142,76%,36%)]/10 flex items-center justify-center shrink-0">
                          <CheckCircle2 className="w-3.5 h-3.5 text-[hsl(142,76%,36%)]" />
                        </div>
                        <div>
                          <p className="font-medium text-sm text-[hsl(222,47%,11%)]">{lesson.title}</p>
                          <p className="text-xs text-slate-500">{lesson.module} • {lesson.date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-slate-600 space-y-3 bg-slate-50 p-4 rounded-xl border border-slate-100">
                    <p className="font-medium text-[hsl(222,47%,11%)]">How to practice:</p>
                    <ol className="list-decimal pl-4 space-y-2 text-xs">
                      <li>Select your current exercise from the roadmap</li>
                      <li>Watch recommended YouTube tutorials if you need inspiration</li>
                      <li>Practice and upload your artwork submission</li>
                      <li>Click <strong className="font-semibold text-slate-700">Mark Exercise as Completed</strong> once you're satisfied with your work</li>
                    </ol>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Upcoming Milestones */}
            <Card className="card-elevated border-0" data-testid="milestones-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-['Outfit'] flex items-center gap-2">
                  <Target className="w-4 h-4 text-[hsl(234,89%,63%)]" />
                  Milestones
                </CardTitle>
              </CardHeader>
              <CardContent>
                {upcomingMilestones.length > 0 ? (
                  <div className="space-y-4">
                    {upcomingMilestones.map((milestone, idx) => (
                      <div key={idx} className="space-y-1.5" data-testid={`milestone-${idx}`}>
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-[hsl(222,47%,11%)]">{milestone.title}</p>
                          <span className="text-xs text-[hsl(262,83%,58%)]">{milestone.progress}%</span>
                        </div>
                        <Progress value={milestone.progress} className="h-1" />
                        {milestone.reward && (
                          <div className="flex items-center gap-1 text-xs text-slate-500">
                            <Award className="w-3 h-3" />
                            {milestone.reward}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">
                    You haven&apos;t unlocked any milestones yet. As you progress, your milestones will appear here.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
